/**
 * Settings → Disposal sites (Phase 16 / Task 16.8, DEC-334, DEC-007). TENANT_ADMIN only
 * (MANAGE_TENANT on every API call).
 *
 * One switch per disposal facility this company hauls to: "Show our customer's name to this
 * disposal company". It controls whether the facility's disposal company sees the operator
 * (customer) name on its lodeX portal data export, daily delivery email and scheduled
 * reports. Off by default; the disposal company never sees it unless this is on.
 *
 * Each switch saves immediately, like the other toggles in Settings. "Select all" and
 * "Clear all" (#40) save the listed sites in one request that changes all of them or none,
 * so a failure never leaves the list half-changed.
 *
 * API: GET /v1/disposal-facility-disclosures, PUT /v1/disposal-facility-disclosures/:facilityId,
 * PUT /v1/disposal-facility-disclosures (bulk).
 */

import * as React from 'react';
import { toast } from 'sonner';
import { useMutation, useQueryClient, type UseMutationResult } from '@tanstack/react-query';
import { authedFetch, useApiOpts, useApiQuery } from '@lodex-pro/web-shared';
import { buttonStyle, cardStyle, errorText, helpText } from '../load-emails/styles';
import { formatNumber } from '@lodex-pro/shared/number-format';

export interface FacilityDisclosure {
  readonly facilityId: string;
  readonly displayName: string;
  readonly city: string | null;
  readonly state: string | null;
  readonly isActive: boolean;
  readonly showOperatorName: boolean;
}

const DISCLOSURES_KEY = ['disposal-facility-disclosures'] as const;

function useSetDisclosure(): UseMutationResult<
  unknown,
  Error,
  { facilityId: string; showOperatorName: boolean }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<unknown, Error, { facilityId: string; showOperatorName: boolean }>({
    mutationFn: async ({ facilityId, showOperatorName }) => {
      const res = await authedFetch(opts, `/v1/disposal-facility-disclosures/${facilityId}`, {
        method: 'PUT',
        body: JSON.stringify({ showOperatorName }),
      });
      if (!res.ok) throw new Error(`Request failed (${String(res.status)})`);
      return (await res.json()) as unknown;
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: DISCLOSURES_KEY }),
  });
}

function useSetAllDisclosures(): UseMutationResult<
  { changed: number },
  Error,
  { facilityIds: readonly string[]; showOperatorName: boolean }
> {
  const opts = useApiOpts();
  const qc = useQueryClient();
  return useMutation<
    { changed: number },
    Error,
    { facilityIds: readonly string[]; showOperatorName: boolean }
  >({
    mutationFn: async ({ facilityIds, showOperatorName }) => {
      const res = await authedFetch(opts, '/v1/disposal-facility-disclosures', {
        method: 'PUT',
        body: JSON.stringify({ facilityIds, showOperatorName }),
      });
      if (!res.ok) throw new Error(`Request failed (${String(res.status)})`);
      return (await res.json()) as { changed: number };
    },
    // Settled, not success: on a failure the list must re-read the saved values too.
    onSettled: () => void qc.invalidateQueries({ queryKey: DISCLOSURES_KEY }),
  });
}

function sites(n: number): string {
  return n === 1 ? '1 site' : `${String(n)} sites`;
}

function place(f: FacilityDisclosure): string {
  return [f.city, f.state].filter((p): p is string => p !== null && p !== '').join(', ');
}

export function DisposalDisclosureSection(): React.JSX.Element {
  const { data, isLoading, isError } = useApiQuery<{
    facilities: readonly FacilityDisclosure[];
  }>(useApiOpts(), '/v1/disposal-facility-disclosures', {
    queryKey: DISCLOSURES_KEY,
    staleTime: 15_000,
  });
  const setDisclosure = useSetDisclosure();
  const setAll = useSetAllDisclosures();
  const [error, setError] = React.useState<string | null>(null);
  const facilities = data?.facilities;
  const busy = setDisclosure.isPending || setAll.isPending;
  const offCount = facilities?.filter((f) => !f.showOperatorName).length ?? 0;
  const onCount = (facilities?.length ?? 0) - offCount;

  // Sends only the sites whose value differs, and only sites on the list the user is looking at.
  const onSetAll = (on: boolean): void => {
    const targets = (facilities ?? []).filter((f) => f.showOperatorName !== on);
    if (targets.length === 0) return;
    setError(null);
    setAll.mutate(
      { facilityIds: targets.map((f) => f.facilityId), showOperatorName: on },
      {
        onSuccess: ({ changed }) =>
          toast.success(
            on
              ? `${sites(changed)} will see your customer names`
              : `${sites(changed)} will no longer see your customer names`,
          ),
        onError: () =>
          setError(
            `Could not save. None of the ${sites(targets.length)} changed. Check your connection and try again.`,
          ),
      },
    );
  };

  const onToggle = (f: FacilityDisclosure, on: boolean): void => {
    setError(null);
    setDisclosure.mutate(
      { facilityId: f.facilityId, showOperatorName: on },
      {
        onSuccess: () =>
          toast.success(
            on
              ? `${f.displayName} will see your customer names`
              : `${f.displayName} will no longer see your customer names`,
          ),
        onError: () => setError('Could not save. Try again.'),
      },
    );
  };

  return (
    <section aria-labelledby="disposal-disclosure" style={cardStyle}>
      <h2 id="disposal-disclosure" style={{ fontSize: '18px', margin: '0 0 4px' }}>
        Customer names at disposal sites
      </h2>
      <p style={helpText}>
        Disposal companies on lodeX see your loads at their sites in their data export, daily email
        and reports. By default they do not see which customer the water came from. Turn a site on
        to show them your customer name for loads you deliver there.
      </p>
      {isLoading && <p style={helpText}>Loading…</p>}
      {isError && (
        <p style={{ ...helpText, color: 'var(--color-danger)' }}>Could not load disposal sites.</p>
      )}
      {facilities !== undefined && facilities.length === 0 && (
        <p style={helpText} data-testid="no-disposal-sites">
          No disposal sites yet. A site appears here once one of your jobs goes there.
        </p>
      )}
      {facilities !== undefined && facilities.length > 1 && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            gap: '12px',
            marginTop: '16px',
          }}
        >
          <button
            type="button"
            style={buttonStyle}
            disabled={busy || offCount === 0}
            onClick={(): void => onSetAll(true)}
          >
            Select all
          </button>
          <button
            type="button"
            style={buttonStyle}
            disabled={busy || onCount === 0}
            onClick={(): void => onSetAll(false)}
          >
            Clear all
          </button>
          <span role="status" style={helpText}>
            {setAll.isPending
              ? `Saving ${sites(setAll.variables.facilityIds.length)}…`
              : `${formatNumber(onCount)} of ${sites(facilities.length)} on`}
          </span>
        </div>
      )}
      {facilities !== undefined && facilities.length > 0 && (
        <ul
          style={{ listStyle: 'none', padding: 0, margin: '16px 0 0', display: 'grid', gap: '8px' }}
        >
          {facilities.map((f) => (
            <li key={f.facilityId}>
              <label
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '12px',
                  minHeight: '56px',
                  fontSize: '16px',
                }}
              >
                <input
                  type="checkbox"
                  style={{ width: '24px', height: '24px' }}
                  checked={f.showOperatorName}
                  disabled={busy}
                  onChange={(e): void => onToggle(f, e.target.checked)}
                />
                <span>
                  Show customer names to <strong>{f.displayName}</strong>
                  {place(f) !== '' && <span style={helpText}> · {place(f)}</span>}
                  {!f.isActive && <span style={helpText}> · inactive</span>}
                </span>
              </label>
            </li>
          ))}
        </ul>
      )}
      {error !== null && (
        <p role="alert" style={errorText}>
          {error}
        </p>
      )}
    </section>
  );
}
